import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Warning as WarningIcon,
  DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';

interface DeleteAllContactsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  contactCount: number;
}

const DeleteAllContactsDialog: React.FC<DeleteAllContactsDialogProps> = ({
  open,
  onClose,
  onConfirm,
  contactCount
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const requiredText = 'ALLE KONTAKTE LÖSCHEN';

  const handleConfirm = async () => {
    if (confirmationText === requiredText) {
      setIsDeleting(true);
      try {
        await onConfirm();
        onClose();
      } catch (error) {
        // Fehler wird vom übergeordneten Component behandelt
      } finally {
        setIsDeleting(false);
        setConfirmationText('');
      }
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setConfirmationText('');
      onClose();
    }
  };

  const isConfirmationValid = confirmationText === requiredText;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          border: '2px solid',
          borderColor: 'error.main'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        bgcolor: 'error.main',
        color: 'error.contrastText'
      }}>
        <WarningIcon />
        Alle Kontakte löschen
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            WARNUNG: Diese Aktion kann nicht rückgängig gemacht werden!
          </Typography>
        </Alert>

        <DialogContentText sx={{ mb: 2 }}>
          Sie sind dabei, <strong>alle {contactCount} Kontakte</strong> unwiderruflich zu löschen.
          Diese Aktion wird alle Kontaktdaten aus der Datenbank entfernen.
        </DialogContentText>

        <DialogContentText sx={{ mb: 3, color: 'error.main', fontWeight: 'medium' }}>
          Um fortzufahren, geben Sie bitte folgenden Text genau ein:
        </DialogContentText>

        <Box sx={{ 
          p: 2, 
          bgcolor: 'grey.100', 
          borderRadius: 1, 
          mb: 2,
          fontFamily: 'monospace',
          textAlign: 'center',
          fontSize: '1.1rem',
          fontWeight: 'bold',
          color: 'error.main'
        }}>
          {requiredText}
        </Box>

        <TextField
          fullWidth
          variant="outlined"
          label="Bestätigungstext eingeben"
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          error={confirmationText.length > 0 && !isConfirmationValid}
          helperText={
            confirmationText.length > 0 && !isConfirmationValid
              ? 'Der eingegebene Text stimmt nicht überein'
              : 'Geben Sie den obigen Text exakt ein'
          }
          disabled={isDeleting}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: 'monospace'
            }
          }}
        />
      </DialogContent>
      
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={isDeleting}
          variant="outlined"
        >
          Abbrechen
        </Button>
        
        <Button
          onClick={handleConfirm}
          disabled={!isConfirmationValid || isDeleting}
          variant="contained"
          color="error"
          startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteSweepIcon />}
          sx={{ minWidth: 120 }}
        >
          {isDeleting ? 'Lösche...' : 'Alle löschen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAllContactsDialog;
